import React, { useState, useEffect } from 'react';
import {
  BarChart3, TrendingUp, DollarSign, Briefcase, Users, Package,
  Calendar, Loader2, Download, Mail, FileBarChart, X, ChevronRight,
} from 'lucide-react';
import { reportsAPI } from '../api/client';
import apiClient from '../api/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

export default function Reports() {
  const [dashboard, setDashboard] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [overview, setOverview] = useState(null);
  const [reportKinds, setReportKinds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [emailTo, setEmailTo] = useState('');
  const [emailing, setEmailing] = useState(false);
  const [activeReport, setActiveReport] = useState(null); // { kind, label, data }
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => { loadReports(); }, [period]);
  useEffect(() => {
    apiClient.get('/api/reports/kinds').then((r) => setReportKinds(r.data || [])).catch(() => {});
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const [dashData, revData, ov] = await Promise.all([
        reportsAPI.getDashboard(period),
        reportsAPI.getRevenue(),
        apiClient.get(`/api/reports/overview?days=${period}`).then((r) => r.data),
      ]);
      setDashboard(dashData);
      setRevenue(revData);
      setOverview(ov);
    } catch (error) {
      console.error('Reports error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openReport = async (kind, label) => {
    setReportLoading(true);
    setActiveReport({ kind, label, data: null });
    try {
      const { data } = await apiClient.get(`/api/reports/custom?kind=${kind}&days=${period}`);
      setActiveReport({ kind, label, data });
    } catch (e) {
      toast.error('Could not load report');
      setActiveReport(null);
    } finally {
      setReportLoading(false);
    }
  };

  const downloadReportCsv = async (kind, label) => {
    try {
      const res = await apiClient.get(
        `/api/reports/custom.csv?kind=${kind}&days=${period}`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label.replace(/\s+/g, '_').toLowerCase()}_report_${period}d.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${a.download}`);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const downloadInvoicesCsv = async () => {
    try {
      const res = await apiClient.get(`/api/reports/invoices.csv?days=${period}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices_report_${period}d.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not download CSV');
    }
  };

  const emailReport = async () => {
    if (!emailTo) return toast.error('Enter an email');
    setEmailing(true);
    try {
      await apiClient.post('/api/reports/email', { to: emailTo, days: period });
      toast.success(`Report emailed to ${emailTo}`);
      setEmailTo('');
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) toast.error('Email not configured. Add RESEND_API_KEY.');
      else toast.error(e?.response?.data?.detail || 'Failed to email report');
    } finally { setEmailing(false); }
  };

  const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const conversionRate = dashboard?.total_jobs > 0 ? (dashboard.completed_jobs / dashboard.total_jobs * 100) : 0;
  const avgRevenue = dashboard?.completed_jobs > 0 ? dashboard.total_revenue / dashboard.completed_jobs : 0;

  // Clickable stat-card config — each card opens its corresponding custom report
  const statCards = [
    { kind: 'revenue', label: 'Revenue Report', icon: DollarSign, value: formatCurrency(dashboard?.total_revenue), sub: `Last ${period} days`, cls: 'stat-green border', iconColor: 'text-green-500', badge: 'Revenue' },
    { kind: 'jobs', label: 'Jobs Report', icon: Briefcase, value: dashboard?.total_jobs || 0, sub: `${dashboard?.completed_jobs || 0} completed`, cls: 'stat-blue border', iconColor: 'text-blue-500', badge: 'Jobs' },
    { kind: 'customers', label: 'Customer Report', icon: Users, value: dashboard?.total_customers || 0, sub: 'Total customers', cls: 'stat-pink border', iconColor: 'text-pink-500', badge: 'CRM' },
    { kind: 'inventory', label: 'Inventory Report', icon: Package, value: dashboard?.total_inventory || 0, sub: `${dashboard?.low_stock_items || 0} low stock`, cls: 'stat-purple border', iconColor: 'text-purple-500', badge: 'Inventory' },
  ];

  return (
    <div className="space-y-6 fade-in" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Reports & Analytics</h1>
          <p className="text-muted-foreground">Click any card to drill into the full report</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={period.toString()} onValueChange={(v) => setPeriod(parseInt(v))}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={downloadInvoicesCsv} data-testid="report-download-csv">
            <Download className="w-4 h-4 mr-1" /> Invoices CSV
          </Button>
        </div>
      </div>

      <Card className="border-dashed">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <Mail className="w-5 h-5 text-sky-500 shrink-0" />
          <span className="text-sm flex-1 min-w-[160px]">Email this report as CSV</span>
          <Input
            type="email" placeholder="accountant@business.com" className="max-w-xs"
            value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="report-email-to"
          />
          <Button onClick={emailReport} disabled={emailing || !emailTo} data-testid="report-email-send">
            {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
            Send
          </Button>
        </CardContent>
      </Card>

      {/* Clickable Key Metrics → opens the corresponding report */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(({ kind, label, icon: Icon, value, sub, cls, iconColor, badge }) => (
          <Card
            key={kind}
            className={`${cls} cursor-pointer card-hover transition-all hover:scale-[1.02]`}
            onClick={() => openReport(kind, label)}
            data-testid={`report-card-${kind}`}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-8 h-8 ${iconColor}`} />
                <span className={`text-xs ${iconColor.replace('text-', 'bg-').replace('-500', '-500/20')} ${iconColor} px-2 py-1 rounded`}>{badge}</span>
              </div>
              <p className="text-3xl font-bold">{value}</p>
              <p className="text-xs text-muted-foreground mt-1 flex items-center justify-between">
                <span>{sub}</span>
                <ChevronRight className="w-4 h-4" />
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Custom Reports row */}
      <Card data-testid="custom-reports-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileBarChart className="w-5 h-5 text-sky-500" /> Custom Reports
          </CardTitle>
          <CardDescription>Pull any metric across your business — open or export as CSV.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {reportKinds.map((rk) => (
              <div
                key={rk.id}
                className="rounded-lg border bg-muted/30 p-3 hover:bg-muted transition flex flex-col gap-2"
                data-testid={`custom-report-${rk.id}`}
              >
                <div className="font-semibold">{rk.label}</div>
                <div className="text-xs text-muted-foreground flex-1">{rk.description}</div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openReport(rk.id, `${rk.label} Report`)} className="flex-1">
                    <BarChart3 className="w-3 h-3 mr-1" /> View
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => downloadReportCsv(rk.id, `${rk.label} Report`)} title="Export CSV">
                    <Download className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Performance summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-hover">
          <CardHeader><CardTitle className="text-base">Job Completion Rate</CardTitle><CardDescription>% completed</CardDescription></CardHeader>
          <CardContent>
            <div className="flex items-end justify-between mb-2">
              <p className="text-4xl font-bold text-green-500">{conversionRate.toFixed(1)}%</p>
              <TrendingUp className="w-6 h-6 text-green-500" />
            </div>
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500" style={{ width: `${conversionRate}%` }} />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardHeader><CardTitle className="text-base">Avg Revenue / Job</CardTitle><CardDescription>Per completed job</CardDescription></CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-blue-500">{formatCurrency(avgRevenue)}</p>
            <p className="text-xs text-muted-foreground mt-2">Based on {dashboard?.completed_jobs || 0} completed jobs</p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardHeader><CardTitle className="text-base">Active Period</CardTitle><CardDescription>Window analyzed</CardDescription></CardHeader>
          <CardContent>
            <div className="flex items-end justify-between mb-2">
              <p className="text-4xl font-bold text-purple-500">{period}</p>
              <Calendar className="w-6 h-6 text-purple-500" />
            </div>
            <p className="text-xs text-muted-foreground">Days</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Trends */}
      <Card>
        <CardHeader><CardTitle>Revenue Trends</CardTitle><CardDescription>Monthly revenue breakdown</CardDescription></CardHeader>
        <CardContent>
          {revenue?.data && revenue.data.length > 0 ? (
            <div className="space-y-3">
              {revenue.data.map((entry, idx) => {
                const month = `${entry._id?.year}-${entry._id?.month}`;
                const maxRev = Math.max(...revenue.data.map(e => e.total_revenue));
                const percentage = maxRev > 0 ? (entry.total_revenue / maxRev * 100) : 0;
                return (
                  <div key={idx} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{month}</span>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-muted-foreground">{entry.job_count} jobs</span>
                        <span className="font-bold text-green-500">{formatCurrency(entry.total_revenue)}</span>
                      </div>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500" style={{ width: `${percentage}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No revenue data yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tech earnings */}
      {overview && (
        <Card data-testid="tech-earnings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-500" /> Technician Earnings & Profit Splits
            </CardTitle>
            <CardDescription>
              Total revenue {formatCurrency(overview.total_revenue)} ·{' '}
              Outstanding {formatCurrency(overview.total_outstanding)} ·{' '}
              {overview.invoices_paid}/{overview.invoices_count} invoices paid
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(!overview.tech_breakdown || overview.tech_breakdown.length === 0) ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No completed jobs with technicians in the last {period} days.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-muted-foreground border-b">
                    <tr>
                      <th className="text-left py-2">Technician</th>
                      <th className="text-right">Jobs</th>
                      <th className="text-right">Revenue</th>
                      <th className="text-right">Tech Earnings</th>
                      <th className="text-right">Company</th>
                      <th className="text-right">Owed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.tech_breakdown.map((t) => (
                      <tr key={t.technician_id} className="border-b last:border-0" data-testid={`tech-row-${t.technician_id}`}>
                        <td className="py-2 font-medium">{t.technician_name}</td>
                        <td className="text-right">{t.jobs_completed}</td>
                        <td className="text-right">{formatCurrency(t.total_revenue)}</td>
                        <td className="text-right text-emerald-500 font-semibold">{formatCurrency(t.tech_earnings)}</td>
                        <td className="text-right text-sky-500">{formatCurrency(t.company_earnings)}</td>
                        <td className="text-right text-amber-500 font-bold">{formatCurrency(t.owed_to_tech)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Drill-down dialog */}
      <Dialog open={!!activeReport} onOpenChange={(o) => !o && setActiveReport(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="report-drilldown-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{activeReport?.label || 'Report'}</span>
              {activeReport && (
                <Button size="sm" variant="outline" onClick={() => downloadReportCsv(activeReport.kind, activeReport.label)}>
                  <Download className="w-3 h-3 mr-1" /> Export CSV
                </Button>
              )}
            </DialogTitle>
            <DialogDescription>Last {period} days</DialogDescription>
          </DialogHeader>
          {reportLoading || !activeReport?.data ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <ReportTable data={activeReport.data} formatCurrency={formatCurrency} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReportTable({ data, formatCurrency }) {
  const rows = data?.rows || [];
  // Summary line above the table for the most useful aggregate
  const summary = [];
  if (data?.total_paid != null) summary.push(`Paid revenue: ${formatCurrency(data.total_paid)}`);
  if (data?.total_outstanding != null) summary.push(`Outstanding: ${formatCurrency(data.total_outstanding)}`);
  if (data?.total_value != null) summary.push(`Inventory value: ${formatCurrency(data.total_value)}`);
  if (data?.total_calls != null) summary.push(`${data.total_calls} calls (${data.inbound}↓ / ${data.outbound}↑)`);
  if (data?.counts) summary.push(...Object.entries(data.counts).map(([k, v]) => `${k}: ${v}`));

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-8">No data for this period.</p>;
  }
  const cols = Object.keys(rows[0]).filter((k) => k !== '_id');

  return (
    <div className="space-y-3">
      {summary.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {summary.map((s, i) => (
            <span key={i} className="text-xs bg-muted px-2 py-1 rounded border">{s}</span>
          ))}
        </div>
      )}
      <div className="overflow-auto max-h-[55vh] border rounded">
        <table className="w-full text-xs">
          <thead className="bg-muted/50 border-b sticky top-0">
            <tr>
              {cols.map((c) => <th key={c} className="text-left py-2 px-2">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 200).map((r, i) => (
              <tr key={i} className="border-b last:border-0">
                {cols.map((c) => (
                  <td key={c} className="py-1.5 px-2 max-w-[200px] truncate">
                    {typeof r[c] === 'number' && (c.includes('cost') || c.includes('total') || c.includes('revenue') || c.includes('earn') || c.includes('spent') || c.includes('price') || c.includes('value'))
                      ? formatCurrency(r[c])
                      : String(r[c] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 200 && (
          <p className="text-xs text-muted-foreground text-center py-2">Showing 200 of {rows.length} rows — export CSV for full data.</p>
        )}
      </div>
    </div>
  );
}
